import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useComplaintFilters } from "@/hooks/useComplaintFilters";
import { PageHeader, RagBadge, StatusBadge, EscalationBadge, EmptyState, DelayBadge, DataAsOf } from "@/components/widgets";
import { ComplaintDrawer } from "@/components/ComplaintDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Download, Search, ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";

const STATUSES = ["Open", "In Progress", "Resolved", "Closed", "Reopened"];
const DEPARTMENTS = ["Sales", "Service", "Parts", "Bodyshop", "Insurance", "Accessories", "Other"];
const ESC_LEVELS = ["5", "4", "3", "2", "1"];
const ESC_LEVEL_OWNERS = { 5: "MD", 4: "VP", 3: "Cluster", 2: "Outlet Mgr", 1: "CCM/CRM" };

const FilterSelect = ({ value, onChange, testId, placeholder, allLabel, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9" data-testid={testId}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{allLabel}</SelectItem>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const FiltersBar = ({ filters, setFilters, outlets, user, extraChips, clearExtra }) => (
  <Card className="p-3 mb-4">
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-2">
      <div className="relative col-span-2 md:col-span-1">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search no / customer / mobile / SA"
          className="pl-8 h-9"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          data-testid="complaints-search-input"
        />
      </div>
      <FilterSelect value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })}
        testId="complaints-filter-status-select" placeholder="Status" allLabel="All statuses"
        options={STATUSES.map((s) => ({ value: s, label: s }))} />
      <FilterSelect value={filters.rag} onChange={(v) => setFilters({ ...filters, rag: v })}
        testId="complaints-filter-rag-select" placeholder="RAG" allLabel="All RAG"
        options={["Red", "Amber", "Green"].map((r) => ({ value: r, label: r }))} />
      <FilterSelect value={filters.escalation_level} onChange={(v) => setFilters({ ...filters, escalation_level: v })}
        testId="complaints-filter-escalation-select" placeholder="Escalation" allLabel="All levels"
        options={ESC_LEVELS.map((l) => ({ value: l, label: `L${l} · ${ESC_LEVEL_OWNERS[l]}` }))} />
      {user?.outlet_scope === "all" && (
        <FilterSelect value={filters.outlet_id} onChange={(v) => setFilters({ ...filters, outlet_id: v })}
          testId="complaints-filter-outlet-select" placeholder="Outlet" allLabel="All outlets"
          options={outlets.map((o) => ({ value: o.id, label: `${o.code} · ${o.name}` }))} />
      )}
      <FilterSelect value={filters.department} onChange={(v) => setFilters({ ...filters, department: v })}
        testId="complaints-filter-department-select" placeholder="Department" allLabel="All departments"
        options={DEPARTMENTS.map((d) => ({ value: d, label: d }))} />
      <Button
        variant={filters.open_only ? "default" : "outline"}
        className={`h-9 ${filters.open_only ? "bg-[hsl(var(--cx-navy))]" : ""}`}
        onClick={() => setFilters({ ...filters, open_only: !filters.open_only })}
        data-testid="complaints-open-only-toggle"
      >
        Open only
      </Button>
    </div>
    {extraChips.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="active-filter-chips">
        {extraChips.map((c) => (
          <span key={c.key}
            className="inline-flex items-center gap-1.5 text-xs bg-[hsl(var(--accent))] border border-[hsl(var(--cx-gold)/0.4)] text-[hsl(var(--cx-navy))] rounded-full pl-2.5 pr-1.5 py-1">
            {c.label}
            <button onClick={() => clearExtra(c.key)} aria-label={`Clear ${c.label}`}
              data-testid={`clear-chip-${c.key}`}
              className="hover:bg-[hsl(var(--cx-gold)/0.25)] rounded-full p-0.5">
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
    )}
  </Card>
);

const ComplaintsTable = ({ items, onOpen }) => (
  <div className="overflow-x-auto">
    <Table className="cx-table min-w-[980px]" data-testid="complaints-table">
      <TableHeader>
        <TableRow>
          <TableHead className="pl-4">Complaint</TableHead>
          <TableHead>Aging</TableHead>
          <TableHead>Esc.</TableHead>
          <TableHead>Customer</TableHead>
          <TableHead>Outlet</TableHead>
          <TableHead>Category</TableHead>
          <TableHead>SA / Owner</TableHead>
          <TableHead>Reported</TableHead>
          <TableHead className="pr-4">Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((c) => (
          <TableRow
            key={c.id}
            className={`cursor-pointer ${c.rag === "Red" && c.is_open ? "row-red" : ""}`}
            data-testid={`complaint-row-${c.complaint_number}`}
            onClick={() => onOpen(c.id)}
          >
            <TableCell className="pl-4 font-medium whitespace-nowrap">
              {c.complaint_number}
              {c.reopened && <span className="ml-1.5 text-[0.6rem] uppercase text-[hsl(var(--cx-crimson))] font-bold">R</span>}
            </TableCell>
            <TableCell>
              {c.delay_class === "oem" ? <DelayBadge delayClass="oem" /> : <RagBadge rag={c.rag} days={c.days_open} />}
            </TableCell>
            <TableCell>
              {c.is_open && c.escalation_level ? <EscalationBadge level={c.escalation_level} /> : <span className="text-muted-foreground text-xs">—</span>}
            </TableCell>
            <TableCell className="max-w-[160px] truncate">{c.customer_name}</TableCell>
            <TableCell className="whitespace-nowrap">{c.outlet_code} <span className="text-muted-foreground text-xs">{c.outlet_name}</span></TableCell>
            <TableCell className="max-w-[150px] truncate">{c.category}</TableCell>
            <TableCell className="max-w-[140px] truncate">{c.current_owner || c.assigned_sa || "—"}</TableCell>
            <TableCell className="whitespace-nowrap tabular">{dayjs(c.date_reported).format("DD MMM YY")}</TableCell>
            <TableCell className="pr-4"><StatusBadge status={c.status} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const TablePagination = ({ page, pages, total, setPage }) => (
  <div className="flex items-center justify-between px-4 py-3 border-t bg-[hsl(var(--cx-surface))]">
    <p className="text-xs text-muted-foreground tabular">
      Page {page} of {pages} · {total.toLocaleString()} results
    </p>
    <div className="flex gap-1.5">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="complaints-prev-page-button">
        <ChevronLeft size={15} />
      </Button>
      <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="complaints-next-page-button">
        <ChevronRight size={15} />
      </Button>
    </div>
  </div>
);

/* Loading / empty / data states without nested ternaries. */
const ComplaintsResults = ({ loading, items, onOpen }) => {
  if (loading) {
    return (
      <div className="p-4 space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={`complaint-skeleton-${i}`} className="h-10" />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return <EmptyState title="No complaints match your filters" subtitle="Try clearing filters or changing the function scope." />;
  }
  return <ComplaintsTable items={items} onOpen={onOpen} />;
};

const buildExtraChips = (extra) => {
  const chips = [];
  if (extra.category) chips.push({ key: "category", label: `Category: ${extra.category}` });
  if (extra.reopened) chips.push({ key: "reopened", label: "Reopened only" });
  if (extra.min_days || extra.max_days)
    chips.push({ key: "min_days", label: `Aging: ${extra.min_days || 0}${extra.max_days ? `–${extra.max_days}` : "+"} days` });
  if (extra.date_from)
    chips.push({ key: "date_from", label: `Reported: ${dayjs(extra.date_from).format("DD MMM")}${extra.date_to ? ` – ${dayjs(extra.date_to).format("DD MMM")}` : " onwards"}` });
  if (extra.closed_from) chips.push({ key: "closed_from", label: `Closed since ${dayjs(extra.closed_from).format("DD MMM")}` });
  return chips;
};

export default function ComplaintsPage() {
  const {
    user, items, total, page, setPage, pages, loading, outlets,
    filters, setFilters, extra, clearExtra, queryParams,
  } = useComplaintFilters();
  const [drawerId, setDrawerId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/complaints/export", { params: { ...queryParams(), page: undefined, page_size: undefined, sort: undefined }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `complaints_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const extraChips = buildExtraChips(extra);
  const openComplaint = (id) => { setDrawerId(id); setDrawerOpen(true); };

  return (
    <div data-testid="complaints-page">
      <PageHeader title="Complaints" subtitle={`${total.toLocaleString()} complaints in scope · read-only · synced from Salesforce`}>
        <DataAsOf className="mr-2 self-center" />
        <Button variant="outline" onClick={doExport} disabled={exporting} data-testid="complaints-export-button">
          {exporting ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Download size={15} className="mr-1.5" />}
          Export Excel
        </Button>
      </PageHeader>

      <FiltersBar filters={filters} setFilters={setFilters} outlets={outlets} user={user}
        extraChips={extraChips} clearExtra={clearExtra} />

      <Card className="overflow-hidden">
        <ComplaintsResults loading={loading} items={items} onOpen={openComplaint} />
        <TablePagination page={page} pages={pages} total={total} setPage={setPage} />
      </Card>

      <ComplaintDrawer complaintId={drawerId} open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  );
}
